import { isNotNull, relations } from "drizzle-orm"
import {
  boolean,
  date,
  index,
  integer,
  jsonb,
  pgSchema,
  primaryKey,
  text,
  timestamp,
  unique,
  varchar,
} from "drizzle-orm/pg-core"
import { email_length, id_length, ulidId } from "./shared"

export const coreExportSchema = pgSchema("core")

export const coreProductGroup = coreExportSchema.table("product_item_group", {
  id: ulidId(), // prefix 'pg-'
  groupType: text("group_type", {
    enum: ["TRANSFER", "EXPORT"],
  }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }),
  createdBy: varchar("created_by", { length: email_length }), // ID for user
})

export const coreProductItem = coreExportSchema.table(
  "product_item",
  {
    // IDENTIFIERS:
    id: ulidId(), // prefix 'prd-', unique system identifier for product
    requestNumber: text("request_number"), // Derived from productGroupId for convenience in reporting
    // Request product only: requestNumber ("NZL2025/REGID/123T") + sequenceNumber => productNumber "NZL2025/REGID/123T, 2"
    productGroupId: varchar("product_item_group_id", {
      length: id_length,
    })
      .notNull() // Need to drop this constraint to allow standalone products
      .references(() => coreProductGroup.id, { onDelete: "cascade" }),
    groupSequence: integer("sequence"),

    commodity: text("commodity").notNull().default(""),

    // Standalone product only: productNumber "P/24/123" + registrationId only
    standaloneProductNumber: varchar("standalone_product_number").unique(), // ONLY populated for standalone products
    registrationId: varchar("registration_id", {
      // For request products, the registrationId is inferred from the owner of the productGroupId
      length: id_length,
    }), // TODO: Add FK to registration for nullable registration_id - only required for standalone products

    // PRODUCT ATTRIBUTES
    description: jsonb("description"),
    productStatement: text("product_statement"), // Unofficial commercial information
    productReference: text("product_reference"), // Comments
    productDeclaration: text("additional_declaration"), // Phyto additional declarations at trade line item level
    attributes: jsonb("attributes"), // Contains ProductAttributes GraphQL type as jsonb object {}
    packaging: jsonb("packaging"), // Contains Package GraphQL type as jsonb object or array
    quantity: jsonb("quantity"),
    // Containers are in separate table as they can be held at request or product level

    eligibilityCountries: jsonb("eligibility_countries"), // Contains ordered jsonb array [] of country codes that the product should be eligible for
    // TODO: Add transfer control declarations, if verifier can edit independently of attributes

    isExhausted: boolean("item_exhausted"),
    tradeLineGroupNumber: integer("trade_line_group_number"),
    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: email_length }), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: email_length }), // ID for user
  },
  (table) => {
    return {
      productGroupMember: index("core_product_group_member_idx")
        .on(table.productGroupId, table.groupSequence)
        .where(isNotNull(table.productGroupId)),
      requestNumberSequence: index("core_product_request_number_sequence_idx")
        .on(table.requestNumber, table.groupSequence)
        .where(isNotNull(table.requestNumber)),
    }
  },
)

export const coreContainer = coreExportSchema.table(
  "container",
  {
    id: ulidId(),
    productGroupId: varchar("product_group_id", {
      length: id_length,
    })
      .notNull()
      .references(() => coreProductGroup.id, { onDelete: "cascade" }),
    productItemId: varchar("product_item_id", {
      length: id_length,
    }).references(() => coreProductItem.id, { onDelete: "cascade" }),

    containerNumber: text("container_number"), // e.g. "NZL2025/REGID/123T"
    seals: jsonb("seal_numbers").notNull(), // Ordered Jsonb array of strings

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: email_length }), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: email_length }), // ID for user
  },
  (table) => {
    return {
      productContainers: index("core_product_containers_idx").on(
        table.productItemId,
      ),
    }
  },
)

export const coreProductProcess = coreExportSchema.table("product_process", {
  id: ulidId(),
  productItemId: varchar("product_item_id", {
    length: id_length,
  }).references(() => coreProductItem.id, { onDelete: "cascade" }),

  processCode: jsonb("process_code"), // value from reference.process.process_code
  sequenceNumber: integer("sequence"),
  attributes: jsonb("attributes"), // Contains ProcessAttributes { additionalTreatment, chemicalDetail, concentration, duration, temperature}

  startDate: date("start_date"),
  endDate: date("end_date"),
  dateOverride: boolean("date_override"),

  // NZ
  nzPremisesRegistration: jsonb("nz_premises_registration"),

  // FOREIGN
  foreignCountryCode: jsonb("foreign_country_code"),
  foreignPremisesName: varchar("foreign_premises_name", { length: 256 }),
  foreignPremisesIdentifier: varchar("foreign_premises_identifier", {
    length: 35,
  }),
  nameOverride: boolean("name_override"),

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by", { length: email_length }), // ID for user
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by", { length: email_length }), // ID for user
})

// TODO: Add coreProductItemRevision / coreProductItemHistory

export const productPaperSource = coreExportSchema.table(
  "product_paper_source",
  {
    id: varchar("id", { length: id_length }).primaryKey(), // prefix 'pps-'
    toItemId: varchar("to_item_id", {
      length: id_length,
    })
      .references(() => coreProductItem.id, { onDelete: "cascade" })
      .notNull(),
    documentNumber: varchar("document_number", { length: 30 }).notNull(),
    itemNumber: integer("item_number").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => ({
    toItemIdx: index("product_paper_source_to_item_idx").on(table.toItemId),
  }),
)

export const productRelations = relations(coreProductItem, ({ many }) => ({
  sourceProducts: many(coreProductItemRelation, {
    relationName: "sourceProducts",
  }),
  childProducts: many(coreProductItemRelation, {
    relationName: "childProducts",
  }),
  paperSources: many(productPaperSource),
  // TODO: Processes, Supporting Documents
}))

export const coreProductItemRelation = coreExportSchema.table(
  "product_item_relation",
  {
    // TODO: Resolve whether relation ID is required as an explicit primary key
    fromId: varchar("from_item_id", {
      length: id_length,
    })
      .references(() => coreProductItem.id, { onDelete: "cascade" })
      .notNull(),
    toId: varchar("to_item_id", {
      length: id_length,
    })
      .references(() => coreProductItem.id, { onDelete: "cascade" })
      .notNull(),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [primaryKey({ columns: [table.fromId, table.toId] })],
)

export const productToProductRelations = relations(
  coreProductItemRelation,
  ({ one }) => ({
    sourceProduct: one(coreProductItem, {
      fields: [coreProductItemRelation.fromId],
      references: [coreProductItem.id],
    }),
    childProduct: one(coreProductItem, {
      fields: [coreProductItemRelation.toId],
      references: [coreProductItem.id],
    }),
  }),
)
export const coreProductClassification = coreExportSchema.table(
  "product_classification",
  {
    id: ulidId(),
    productItemId: varchar("product_item_id", {
      length: id_length,
    }).references(() => coreProductItem.id, { onDelete: "cascade" }),

    fieldUniqueId: varchar("field_unique_id", { length: 50 }) // fuid
      .notNull(),
    // .references(() => refField.fieldUniqueId, { onDelete: "no action", onUpdate: "cascade", }), Keep FK implicit
    // sequence: integer("sequence").notNull().default(1), // TODO - if we were to use this to hold multi-select then would need sequence

    itemCode: varchar("item_code", { length: 50 }), // Backend code if selected from dropdown list
    value: text("value"), // Display value if selected from dropdown list, entered value if free text. Null if not provided

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    // Dropped as audit history captured on product - createdBy: varchar("created_by", { length: id_length }), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    // Dropped as audit history captured on product - updatedBy: varchar("updated_by", { length: id_length }), // ID for user
  },
  (table) => ({
    productClassificationCode: unique("core_product_classification_idx")
      .on(
        table.productItemId,
        table.fieldUniqueId,
        //table.sequence,
      )
      .nullsNotDistinct(),
  }),
)

export const productProcessHistory = coreExportSchema.table(
  "product_process_history",
  {
    id: ulidId(), // prefix 'pph-'
    productItemId: varchar("product_item_id", {
      length: id_length,
    })
      .notNull()
      .references(() => coreProductItem.id, { onDelete: "cascade" }),
    processCode: varchar("process_code", { length: 3 }).notNull(),
    attributes: jsonb("attributes"),
    earliestStartDate: date("earliest_start_date"),
    latestEndDate: date("latest_end_date"),
    lastProcessStartDate: date("last_process_start_date"),
    lastProcessEndDate: date("last_process_end_date"),

    premisesIdentifier: varchar("premises_identifier", {
      length: 35,
    }).notNull(),
    premisesRegistrationId: varchar("premises_registration_id", {
      length: id_length,
    }),
    premisesName: varchar("premises_name", { length: 256 }),
    countryCode: varchar("country_code", { length: 2 }).notNull(),

    owned: boolean("owned").notNull().default(false),
    concatenated: boolean("concatenated").notNull().default(false),
    latestSubmissionDate: timestamp("latest_submission_date", {
      withTimezone: true,
    }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => ({
    productProcessHistoryIdx: index("product_process_history_idx").on(
      table.productItemId,
    ),
    productProcessHistoryNaturalKey: unique(
      "product_process_history_natural_key",
    )
      .on(
        table.productItemId,
        table.processCode,
        table.premisesIdentifier,
        table.countryCode,
      )
      .nullsNotDistinct(),
  }),
)
